/**
 * Track natural aspect ratios (width/height) of media, measured from the
 * thumbnail <img> as it loads — zero extra network. Ratios are cached in a
 * module-level map keyed by the media key so they persist across renders and
 * re-mounts (e.g. paging back to an event).
 *
 * Until a photo's real ratio is known we use a default so the layout can render
 * immediately; the layout re-flows as measurements arrive.
 */
import { useCallback, useState } from 'react';

const ratioCache = new Map<string, number>();

export function getCachedRatio(key: string): number | undefined {
  return ratioCache.get(key);
}

export function useAspectRatios() {
  // A version counter bumps to trigger a re-layout when new ratios land.
  // Callers must include `version` in their layout useMemo deps so the layout
  // recomputes as measurements arrive (getRatio is a stable fn, so it alone
  // can't signal change).
  const [version, setVersion] = useState(0);

  const report = useCallback((key: string, width: number, height: number) => {
    if (!width || !height) return;
    const ratio = width / height;
    const prev = ratioCache.get(key);
    // Only re-render if this is new or meaningfully different.
    if (prev === undefined || Math.abs(prev - ratio) > 0.01) {
      ratioCache.set(key, ratio);
      setVersion((v) => v + 1);
    }
  }, []);

  return { getRatio: getCachedRatio, report, version };
}

/** Test helper. */
export function _clearRatioCache() {
  ratioCache.clear();
}
